import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { getMomoManagedGirls, getUsersByRole } from '../../utils/roleManager';
import { collection, query, where, getDocs, orderBy, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import { 
  createMomoToGirlRequest, 
  getMomoRequests, 
  acceptTeamRequest, 
  rejectTeamRequest,
  cancelTeamRequest,
  enrichRequestsWithUserData,
  getAvailableGirls,
  REQUEST_STATUS 
} from '../../utils/teamRequestManager';

const MomoDashboard = () => {
  const { currentUser } = useAuth();
  const [managedGirls, setManagedGirls] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('girls'); // girls, bookings, revenue, team, booking-management
  const [selectedGirl, setSelectedGirl] = useState(null);
  const [teamRequests, setTeamRequests] = useState([]);
  const [availableGirls, setAvailableGirls] = useState([]);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [selectedGirlToInvite, setSelectedGirlToInvite] = useState('');
  const [inviteMessage, setInviteMessage] = useState('');

  useEffect(() => {
    fetchMomoData();
    fetchTeamRequests();
    fetchAvailableGirls();
  }, [currentUser]);

  const fetchMomoData = async () => {
    if (!currentUser) return;
    
    setLoading(true);
    try {
      // ดึงรายการน้องที่ดูแล
      const girls = await getMomoManagedGirls(currentUser.uid);
      setManagedGirls(girls);
      
      // ดึงการจองของน้องที่ดูแล
      if (girls.length > 0) {
        const girlIds = girls.map(girl => girl.id);
        const bookingsRef = collection(db, 'bookings');
        const q = query(
          bookingsRef,
          where('girlId', 'in', girlIds)
        );
        const snapshot = await getDocs(q);
        const bookingsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // Sort by createdAt descending
        const sortedBookings = bookingsData.sort((a, b) => {
          const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt);
          const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt);
          return dateB - dateA;
        });
        
        setBookings(sortedBookings);
      }
    } catch (error) {
      console.error('Error fetching momo data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTeamRequests = async () => {
    if (!currentUser) return;
    
    try {
      const requests = await getMomoRequests(currentUser.uid);
      const enrichedRequests = await enrichRequestsWithUserData(requests);
      setTeamRequests(enrichedRequests);
    } catch (error) {
      console.error('Error fetching team requests:', error);
    }
  };


  const fetchAvailableGirls = async () => {
    try {
      const girls = await getAvailableGirls();
      setAvailableGirls(girls);
    } catch (error) {
      console.error('Error fetching available girls:', error);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('th-TH', {
      style: 'currency',
      currency: 'THB'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('th-TH', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'confirmed': return 'ยืนยันแล้ว';
      case 'pending': return 'รอยืนยัน';
      case 'rejected': return 'ปฏิเสธ';
      case 'completed': return 'เสร็จสิ้น';
      default: return 'ไม่ทราบ';
    }
  };

  const handleSendInvite = async (e) => {
    e.preventDefault();
    if (!selectedGirlToInvite) {
      alert('กรุณาเลือกน้อง');
      return;
    }

    try {
      await createMomoToGirlRequest(currentUser.uid, selectedGirlToInvite, inviteMessage);
      alert('ส่งคำเชิญเข้าร่วมทีมสำเร็จ!');
      setShowInviteModal(false);
      setSelectedGirlToInvite('');
      setInviteMessage('');
      fetchTeamRequests(); // รีเฟรชข้อมูล
      fetchAvailableGirls(); // รีเฟรชรายการน้องที่ว่าง
    } catch (error) {
      console.error('Error sending invite:', error);
      alert('เกิดข้อผิดพลาด: ' + error.message);
    }
  };


  const handleAcceptRequest = async (requestId) => {
    if (!window.confirm('คุณแน่ใจหรือไม่ที่จะยอมรับคำขอเข้าร่วมทีมนี้?')) {
      return;
    }
    
    try {
      await acceptTeamRequest(requestId);
      alert('ยอมรับคำขอเข้าร่วมทีมสำเร็จ!');
      fetchTeamRequests();
      fetchMomoData(); // รีเฟรชข้อมูลน้องที่ดูแล
    } catch (error) {
      console.error('Error accepting request:', error);
      alert('เกิดข้อผิดพลาด: ' + error.message);
    }
  };

  const handleRejectRequest = async (requestId) => {
    if (!window.confirm('คุณแน่ใจหรือไม่ที่จะปฏิเสธคำขอเข้าร่วมทีมนี้?')) {
      return;
    }
    
    try {
      await rejectTeamRequest(requestId);
      alert('ปฏิเสธคำขอเข้าร่วมทีมสำเร็จ!');
      fetchTeamRequests();
    } catch (error) {
      console.error('Error rejecting request:', error);
      alert('เกิดข้อผิดพลาด: ' + error.message);
    }
  };

  const handleCancelRequest = async (requestId) => {
    if (!window.confirm('คุณแน่ใจหรือไม่ที่จะลบคำขอนี้ออกจากรายการ?')) {
      return;
    }
    
    try {
      await cancelTeamRequest(requestId);
      alert('ลบคำขอสำเร็จ!');
      fetchTeamRequests();
    } catch (error) {
      console.error('Error cancelling request:', error);
      alert('เกิดข้อผิดพลาด: ' + error.message);
    }
  };

  // ฟังก์ชันอัปเดตสถานะการจองแทนน้อง
  const updateBookingStatusForGirl = async (bookingId, newStatus, girlId) => {
    try {
      const bookingRef = doc(db, 'bookings', bookingId);
      await updateDoc(bookingRef, {
        status: newStatus,
        updatedAt: new Date(),
        updatedBy: currentUser.uid,
        updatedByRole: 'momo'
      });
      
      // อัปเดตสถานะในรายการ bookings
      setBookings(prevBookings => 
        prevBookings.map(booking => 
          booking.id === bookingId 
            ? { ...booking, status: newStatus, updatedAt: new Date() }
            : booking
        )
      );
      
      alert(`อัปเดตสถานะการจองเป็น "${getStatusText(newStatus)}" สำเร็จ!`);
    } catch (error) {
      console.error('Error updating booking status:', error);
      alert('เกิดข้อผิดพลาดในการอัปเดตสถานะการจอง');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg text-gray-600">กำลังโหลดข้อมูล...</div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <h1 className="text-3xl font-bold text-center mb-8">📊 Hugkan - แดชบอร์ดโม (Manager)</h1>
      
      {/* สถิติสรุป */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-gradient-to-r from-purple-400 to-purple-600 text-white p-6 rounded-lg shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm">น้องที่ดูแล</p>
              <p className="text-2xl font-bold">{managedGirls.length}</p>
            </div>
            <div className="text-4xl">👥</div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-blue-400 to-blue-600 text-white p-6 rounded-lg shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm">การจองทั้งหมด</p>
              <p className="text-2xl font-bold">{bookings.length}</p>
            </div>
            <div className="text-4xl">📋</div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-green-400 to-green-600 text-white p-6 rounded-lg shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm">การจองวันนี้</p>
              <p className="text-2xl font-bold">
                {bookings.filter(booking => {
                  const today = new Date().toDateString();
                  const bookingDate = new Date(booking.bookingDate).toDateString();
                  return today === bookingDate;
                }).length}
              </p>
            </div>
            <div className="text-4xl">📅</div>
          </div>
        </div>
      </div>

      {/* แท็บเมนู */}
      <div className="flex space-x-4 mb-6">
        <button
          onClick={() => setActiveTab('girls')}
          className={`px-6 py-3 rounded-lg font-medium transition ${
            activeTab === 'girls'
              ? 'bg-purple-500 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          👥 น้องที่ดูแล
        </button>
        <button
          onClick={() => setActiveTab('bookings')}
          className={`px-6 py-3 rounded-lg font-medium transition ${
            activeTab === 'bookings'
              ? 'bg-purple-500 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          📋 การจองคิว
        </button>
        <button
          onClick={() => setActiveTab('revenue')}
          className={`px-6 py-3 rounded-lg font-medium transition ${
            activeTab === 'revenue'
              ? 'bg-purple-500 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          💰 รายได้
        </button>
        <button
          onClick={() => setActiveTab('team')}
          className={`px-6 py-3 rounded-lg font-medium transition ${
            activeTab === 'team'
              ? 'bg-purple-500 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          👥 จัดการทีม
        </button>
        <button
          onClick={() => setActiveTab('booking-management')}
          className={`px-6 py-3 rounded-lg font-medium transition ${
            activeTab === 'booking-management'
              ? 'bg-purple-500 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          ⚙️ จัดการคิวแทนน้อง
        </button>
      </div>

      {/* เนื้อหาตามแท็บที่เลือก */}
      {activeTab === 'girls' && (
        <div className="bg-white rounded-lg shadow-md">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold">👥 น้องที่ดูแล</h2>
            <p className="text-gray-600 text-sm mt-1">
              รายการน้องที่อยู่ในสังกัดของคุณ
            </p>
          </div>
          
          <div className="p-6">
            {managedGirls.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>ยังไม่มีน้องที่ดูแล</p>
                <p className="text-sm">ติดต่อแอดมินเพื่อเพิ่มน้องให้ดูแล</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {managedGirls.map((girl) => (
                  <div key={girl.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition">
                    <div className="text-center">
                      <div className="w-16 h-16 bg-pink-200 rounded-full mx-auto mb-3 flex items-center justify-center">
                        <span className="text-2xl">👧</span>
                      </div>
                      <h3 className="font-semibold text-lg mb-2">{girl.nickname}</h3>
                      <div className="text-sm text-gray-600 space-y-1">
                        <p>เกรด: ⭐⭐⭐ {girl.grades}</p>
                        <p>ประเภท: {girl.type === 'normal' ? 'ธรรมดา' : girl.type === 'vn' ? 'วีเอ็น' : girl.type === 'strong' ? 'แรง' : 'อัพ'}</p>
                        <div className="space-y-1">
                          <p className="text-green-600 font-semibold">
                            💰 {girl.servicePrice || girl.prices?.servicePrice || 1500} บาท/ชม.
                          </p>
                          <p className="text-blue-600 font-semibold">
                            🪙 {girl.coinPrice || girl.prices?.coinPrice || girl.prices?.base || 50} เหรียญ/ชม.
                          </p>
                        </div>
                        <p>สถานที่: {girl.location}</p>
                        <p className={`text-sm font-semibold ${
                          girl.status === 'available' ? 'text-green-600' : 'text-red-600'
                        }`}>
                          สถานะ: {girl.status === 'available' ? 'พร้อมรับงาน' : 'ไม่พร้อมรับงาน'}
                        </p>
                      </div>
                      <div className="mt-4 space-y-2">
                        <button
                          onClick={() => {
                            setSelectedGirl(girl);
                            setActiveTab('bookings');
                          }}
                          className="w-full bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-600 transition text-sm"
                        >
                          ดูการจอง
                        </button>
                        <button
                          onClick={() => {
                            setSelectedGirl(girl);
                            setActiveTab('revenue');
                          }}
                          className="w-full bg-green-500 text-white py-2 px-4 rounded hover:bg-green-600 transition text-sm"
                        >
                          ดูรายได้
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'bookings' && (
        <div className="bg-white rounded-lg shadow-md">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold">📋 การจองคิว</h2>
            <p className="text-gray-600 text-sm mt-1">
              {selectedGirl ? `การจองของ ${selectedGirl.nickname}` : 'การจองของน้องทั้งหมดที่ดูแล'}
            </p>
            {selectedGirl && (
              <button
                onClick={() => setSelectedGirl(null)}
                className="mt-2 text-sm text-blue-600 hover:text-blue-800"
              >
                ← ดูการจองทั้งหมด
              </button>
            )}
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    วันที่จอง
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    น้อง
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ลูกค้า
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    เวลา
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ระยะเวลา
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ราคา
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    สถานะ
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {bookings
                  .filter(booking => !selectedGirl || booking.girlId === selectedGirl.id)
                  .map((booking) => (
                  <tr key={booking.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(booking.bookingDate)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {booking.girlName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {booking.userName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {booking.bookingTime}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {booking.duration} ชม.
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="space-y-1">
                        <p className="text-green-600 font-semibold">
                          {formatCurrency(booking.pricingInfo?.totalServicePrice || booking.totalPrice * 30)}
                        </p>
                        <p className="text-blue-600 text-xs">
                          {booking.pricingInfo?.totalCoinPrice || booking.totalPrice} เหรียญ
                        </p>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(booking.status)}`}>
                        {getStatusText(booking.status)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {bookings.filter(booking => !selectedGirl || booking.girlId === selectedGirl.id).length === 0 && (
            <div className="text-center py-8 text-gray-500">
              ไม่มีการจอง{selectedGirl ? `ของ ${selectedGirl.nickname}` : ''}
            </div>
          )}
        </div>
      )}

      {activeTab === 'revenue' && (
        <div className="bg-white rounded-lg shadow-md">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold">💰 รายได้</h2>
            <p className="text-gray-600 text-sm mt-1">
              {selectedGirl ? `รายได้ของ ${selectedGirl.nickname}` : 'รายได้ของน้องทั้งหมดที่ดูแล'}
            </p>
            {selectedGirl && (
              <button
                onClick={() => setSelectedGirl(null)}
                className="mt-2 text-sm text-blue-600 hover:text-blue-800"
              >
                ← ดูรายได้ทั้งหมด
              </button>
            )}
          </div>
          
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h3 className="text-lg font-semibold text-green-800 mb-2">💰 รายได้รวม</h3>
                <p className="text-2xl font-bold text-green-600">
                  {formatCurrency(
                    bookings
                      .filter(booking => !selectedGirl || booking.girlId === selectedGirl.id)
                      .reduce((total, booking) => {
                        return total + (booking.pricingInfo?.girlRevenue || (booking.totalPrice * 30 * 0.7));
                      }, 0)
                  )}
                </p>
              </div>
              
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h3 className="text-lg font-semibold text-blue-800 mb-2">📊 การจองที่เสร็จสิ้น</h3>
                <p className="text-2xl font-bold text-blue-600">
                  {bookings
                    .filter(booking => (!selectedGirl || booking.girlId === selectedGirl.id) && booking.status === 'completed')
                    .length}
                </p>
              </div>
              
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h3 className="text-lg font-semibold text-purple-800 mb-2">⏳ รอยืนยัน</h3>
                <p className="text-2xl font-bold text-purple-600">
                  {bookings
                    .filter(booking => (!selectedGirl || booking.girlId === selectedGirl.id) && booking.status === 'pending')
                    .length}
                </p>
              </div>
            </div>
            
            <div className="text-center text-gray-600">
              <p className="text-sm">
                💡 รายได้นี้เป็นรายได้ของน้องที่คุณดูแล
              </p>
              <p className="text-xs mt-1">
                ระบบจะคำนวณรายได้ตามค่าคอมมิชชั่นที่กำหนด
              </p>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'team' && (
        <div className="bg-white rounded-lg shadow-md">
          <div className="p-6 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold">👥 จัดการทีม</h2>
              <button
                onClick={() => setShowInviteModal(true)}
                className="bg-purple-500 text-white px-4 py-2 rounded hover:bg-purple-600 transition"
              >
                เชิญน้องเข้าทีม
              </button>
            </div>
            <p className="text-gray-600 text-sm mt-1">
              จัดการการเข้าร่วมทีมของน้อง
            </p>
          </div>
          
          <div className="p-6">
            <div className="space-y-6">
              {/* คำขอเข้าร่วมทีมที่รอยืนยัน */}
              <div>
                <h3 className="text-lg font-semibold mb-4">คำขอเข้าร่วมทีมที่รอยืนยัน</h3>
                {teamRequests.filter(req => req.status === REQUEST_STATUS.PENDING).length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <p>ไม่มีคำขอเข้าร่วมทีมที่รอยืนยัน</p>
                    <p className="text-sm">คลิกปุ่ม "เชิญน้องเข้าทีม" เพื่อส่งคำเชิญใหม่</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {teamRequests
                      .filter(req => req.status === REQUEST_STATUS.PENDING)
                      .map((request) => (
                      <div key={request.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className="font-semibold">
                              {request.type === 'girl_to_momo' ? 'คำขอจากน้อง' : 'คำเชิญของคุณ'}
                            </h4>
                            <p className="text-sm text-gray-600">
                              {request.type === 'girl_to_momo' 
                                ? `ขอเข้าร่วมทีม: ${request.girlData?.nickname || request.girlData?.email}`
                                : `เชิญ: ${request.girlData?.nickname || request.girlData?.email}`
                              }
                            </p>
                            {request.message && (
                              <p className="text-sm text-gray-500 mt-1">"{request.message}"</p>
                            )}
                            <p className="text-xs text-gray-400 mt-1">
                              ส่งเมื่อ: {formatDate(request.createdAt.toDate())}
                            </p>
                          </div>
                          <div className="flex space-x-2">
                            {request.type === 'girl_to_momo' && request.status === 'pending' && (
                              <>
                                <button
                                  onClick={() => handleAcceptRequest(request.id)}
                                  className="bg-green-500 text-white px-3 py-1 rounded text-sm hover:bg-green-600 transition"
                                >
                                  ยอมรับ
                                </button>
                                <button
                                  onClick={() => handleRejectRequest(request.id)}
                                  className="bg-red-500 text-white px-3 py-1 rounded text-sm hover:bg-red-600 transition"
                                >
                                  ปฏิเสธ
                                </button>
                              </>
                            )}
                            {request.type === 'momo_to_girl' && request.status === 'pending' && (
                              <button
                                onClick={() => handleCancelRequest(request.id)}
                                className="bg-gray-500 text-white px-3 py-1 rounded text-sm hover:bg-gray-600 transition"
                              >
                                ยกเลิก
                              </button>
                            )}
                            {request.status !== 'pending' && request.status !== 'cancelled' && (
                              <button
                                onClick={() => handleCancelRequest(request.id)}
                                className="bg-gray-500 text-white px-3 py-1 rounded text-sm hover:bg-gray-600 transition"
                              >
                                ลบ
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>


              {/* ประวัติคำขอ */}
              <div>
                <h3 className="text-lg font-semibold mb-4">ประวัติคำขอเข้าร่วมทีม</h3>
                {teamRequests.filter(req => req.status !== REQUEST_STATUS.PENDING).length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <p>ไม่มีประวัติคำขอเข้าร่วมทีม</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {teamRequests
                      .filter(req => req.status !== REQUEST_STATUS.PENDING)
                      .map((request) => (
                      <div key={request.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className="font-semibold">
                              {request.type === 'girl_to_momo' ? 'คำขอจากน้อง' : 'คำเชิญของคุณ'}
                            </h4>
                            <p className="text-sm text-gray-600">
                              {request.type === 'girl_to_momo' 
                                ? `ขอเข้าร่วมทีม: ${request.girlData?.nickname || request.girlData?.email}`
                                : `เชิญ: ${request.girlData?.nickname || request.girlData?.email}`
                              }
                            </p>
                            {request.message && (
                              <p className="text-sm text-gray-500 mt-1">"{request.message}"</p>
                            )}
                            <p className="text-xs text-gray-400 mt-1">
                              ส่งเมื่อ: {formatDate(request.createdAt.toDate())}
                            </p>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              request.status === REQUEST_STATUS.ACCEPTED 
                                ? 'bg-green-100 text-green-800'
                                : request.status === REQUEST_STATUS.REJECTED
                                ? 'bg-red-100 text-red-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {request.status === REQUEST_STATUS.ACCEPTED ? 'ยอมรับแล้ว' :
                               request.status === REQUEST_STATUS.REJECTED ? 'ปฏิเสธ' : 'ยกเลิก'}
                            </span>
                            {request.status !== 'cancelled' && (
                              <button
                                onClick={() => handleCancelRequest(request.id)}
                                className="bg-gray-500 text-white px-2 py-1 rounded text-xs hover:bg-gray-600 transition"
                              >
                                ลบ
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal เชิญน้องเข้าทีม */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">เชิญน้องเข้าทีม</h3>
            
            <form onSubmit={handleSendInvite} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  เลือกน้อง
                </label>
                <select
                  value={selectedGirlToInvite}
                  onChange={(e) => setSelectedGirlToInvite(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  required
                >
                  <option value="">เลือกน้อง</option>
                  {availableGirls.map(girl => (
                    <option key={girl.id} value={girl.id}>
                      {girl.nickname} ({girl.type === 'normal' ? 'ธรรมดา' : girl.type === 'vn' ? 'วีเอ็น' : girl.type === 'strong' ? 'แรง' : 'อัพ'})
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  ข้อความเพิ่มเติม (ไม่บังคับ)
                </label>
                <textarea
                  value={inviteMessage}
                  onChange={(e) => setInviteMessage(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  rows={3}
                  placeholder="บอกน้องเกี่ยวกับทีมของคุณ..."
                />
              </div>
              
              <div className="flex space-x-4">
                <button
                  type="submit"
                  className="flex-1 bg-purple-500 text-white py-2 rounded hover:bg-purple-600 transition"
                >
                  ส่งคำเชิญ
                </button>
                <button
                  type="button"
                  onClick={() => setShowInviteModal(false)}
                  className="flex-1 bg-gray-500 text-white py-2 rounded hover:bg-gray-600 transition"
                >
                  ยกเลิก
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {activeTab === 'booking-management' && (
        <div className="bg-white rounded-lg shadow-md">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold">⚙️ จัดการคิวแทนน้อง</h2>
            <p className="text-gray-600 text-sm mt-1">
              จัดการการจองแทนน้องที่อนุญาตให้คุณช่วยจัดการคิว
            </p>
          </div>
          
          <div className="p-6">
            <div className="space-y-6">
              {/* รายการน้องที่อนุญาตให้จัดการคิว */}
              <div>
                <h3 className="text-lg font-semibold mb-4">น้องที่อนุญาตให้จัดการคิว</h3>
                {managedGirls.filter(girl => girl.bookingManagementMode === 'self_with_momo').length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <p>ไม่มีน้องที่อนุญาตให้คุณจัดการคิว</p>
                    <p className="text-sm mt-2">น้องต้องตั้งค่า "จัดการคิวด้วยตัวเอง และให้โมช่วยจัดการคิว" ก่อน</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {managedGirls
                      .filter(girl => girl.bookingManagementMode === 'self_with_momo')
                      .map((girl) => (
                        <div key={girl.id} className="bg-gray-50 p-4 rounded-lg border">
                          <div className="flex items-center space-x-3 mb-3">
                            <img
                              src={girl.profileImage || 'https://via.placeholder.com/50x50?text=👧'}
                              alt={girl.nickname}
                              className="w-12 h-12 rounded-full object-cover"
                            />
                            <div>
                              <h4 className="font-semibold">{girl.nickname}</h4>
                              <p className="text-sm text-gray-600">
                                {girl.type === 'normal' ? 'ธรรมดา' : 
                                 girl.type === 'vn' ? 'วีเอ็น' : 
                                 girl.type === 'strong' ? 'แรง' : 'อัพ'}
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={() => setSelectedGirl(girl)}
                            className="w-full bg-purple-500 text-white py-2 rounded hover:bg-purple-600 transition"
                          >
                            จัดการคิวของ {girl.nickname}
                          </button>
                        </div>
                      ))}
                  </div>
                )}
              </div>

              {/* การจองของน้องที่เลือก */}
              {selectedGirl && (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold">
                      การจองของ {selectedGirl.nickname}
                    </h3>
                    <button
                      onClick={() => setSelectedGirl(null)}
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      ← กลับไปรายการน้อง
                    </button>
                  </div>
                  
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse border border-gray-300">
                      <thead>
                        <tr className="bg-gray-100">
                          <th className="border border-gray-300 px-4 py-2 text-left">ลูกค้า</th>
                          <th className="border border-gray-300 px-4 py-2 text-left">วันที่</th>
                          <th className="border border-gray-300 px-4 py-2 text-left">เวลา</th>
                          <th className="border border-gray-300 px-4 py-2 text-left">สถานะ</th>
                          <th className="border border-gray-300 px-4 py-2 text-left">ราคา</th>
                          <th className="border border-gray-300 px-4 py-2 text-left">จัดการ</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bookings
                          .filter(booking => booking.girlId === selectedGirl.id)
                          .map((booking) => (
                            <tr key={booking.id}>
                              <td className="border border-gray-300 px-4 py-2">
                                {booking.customerName || 'ไม่ระบุ'}
                              </td>
                              <td className="border border-gray-300 px-4 py-2">
                                {new Date(booking.bookingDate).toLocaleDateString('th-TH')}
                              </td>
                              <td className="border border-gray-300 px-4 py-2">
                                {booking.bookingTime} - {booking.endTime || 'ไม่ระบุ'}
                              </td>
                              <td className="border border-gray-300 px-4 py-2">
                                <span className={`px-2 py-1 rounded text-xs ${
                                  booking.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                                  booking.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                  booking.status === 'rejected' ? 'bg-red-100 text-red-800' :
                                  booking.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {getStatusText(booking.status)}
                                </span>
                              </td>
                              <td className="border border-gray-300 px-4 py-2">
                                {booking.pricingInfo?.totalPrice ? 
                                  formatCurrency(booking.pricingInfo.totalPrice) : 
                                  'ไม่ระบุ'
                                }
                              </td>
                              <td className="border border-gray-300 px-4 py-2">
                                <div className="flex space-x-2">
                                  {booking.status === 'pending' && (
                                    <>
                                      <button
                                        onClick={() => {
                                          if (window.confirm('คุณแน่ใจหรือไม่ที่จะยืนยันการจองนี้?')) {
                                            updateBookingStatusForGirl(booking.id, 'confirmed', selectedGirl.id);
                                          }
                                        }}
                                        className="px-3 py-1 bg-green-500 text-white text-xs rounded hover:bg-green-600"
                                      >
                                        ยืนยัน
                                      </button>
                                      <button
                                        onClick={() => {
                                          if (window.confirm('คุณแน่ใจหรือไม่ที่จะปฏิเสธการจองนี้?')) {
                                            updateBookingStatusForGirl(booking.id, 'rejected', selectedGirl.id);
                                          }
                                        }}
                                        className="px-3 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600"
                                      >
                                        ปฏิเสธ
                                      </button>
                                    </>
                                  )}
                                  {booking.status === 'confirmed' && (
                                    <button
                                      onClick={() => {
                                        if (window.confirm('คุณแน่ใจหรือไม่ที่จะทำเครื่องหมายว่าการจองเสร็จสิ้น?')) {
                                          updateBookingStatusForGirl(booking.id, 'completed', selectedGirl.id);
                                        }
                                      }}
                                      className="px-3 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600"
                                    >
                                      เสร็จสิ้น
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                  
                  {bookings.filter(booking => booking.girlId === selectedGirl.id).length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      ไม่มีการจองของ {selectedGirl.nickname}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MomoDashboard;
